import ApartmentIcon from "@mui/icons-material/Apartment";
import {
  Alert,
  AppBar,
  AppBarProps,
  Avatar,
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  MenuProps,
  Toolbar,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { FC } from "react";
import { MouseEvent, useEffect, useState } from "react";
import useAuth from "../../hooks/useAuth";
import useOrgSelectionModal from "../../hooks/useOrgSelectionModal";
import useSettings from "../../hooks/useSettings";
import ChevronDown from "../../icons/ChevronDown";
import MenuIcon from "../../icons/Menu";
import { Organization } from "../../types/organization";
import ActionsMenu from "../ActionsMenu";
import NotificationCenter from "../notifications/NotificationCenter";
import AccountPopover from "./AccountPopover";
import ContentSearch from "./ContentSearch";
import OrganizationTier from "./OrganizationTier";

interface DashboardNavbarProps extends AppBarProps {
  onSidebarMobileOpen?: () => void;
}

const DashboardNavbarRoot = styled(AppBar)(({ theme }) => ({
  height: 56,
  display: "flex",
  alignContent: "center",
  ...(theme.palette.mode === "light" && {
    backgroundColor: theme.palette.primary.main,
    boxShadow: "none",
    color: theme.palette.primary.contrastText,
  }),
  ...(theme.palette.mode === "dark" && {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  }),
  zIndex: theme.zIndex.appBar,
}));

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

const DashboardNavbar: FC<DashboardNavbarProps> = (props) => {
  const auth = useAuth();
  useFlags();
  const { settings } = useSettings();
  const { openOrgSelectionModal } = useOrgSelectionModal();
  const [organizations, setOrganizations] = useState<Organization[]>();
  const [currentOrg, setCurrentOrg] = useState<Organization>();
  const { onSidebarMobileOpen, ...other } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const { organizations: authOrgs } = auth;
    const { selectedOrganization } = settings;
    if (selectedOrganization) {
      const selectedOrg: Organization = authOrgs?.find(
        (o: Organization) => o.id === selectedOrganization.id,
      );
      setOrganizations(authOrgs);
      setCurrentOrg(selectedOrg);
    }
  }, [auth, organizations, settings]);

  const { appAlert,allowNotifications } = useFlags();
  const orgMenuOpen = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const triggerFacilityChange = () => {
    openOrgSelectionModal();
    handleClose();
  };

  return (
    <DashboardNavbarRoot {...other}>
      <Toolbar
        sx={{
          minHeight: "56px !important",
          height: "56px !important",
          display: "flex",
          paddingLeft: "10px !important",
        }}
      >
        <IconButton
          color="inherit"
          onClick={onSidebarMobileOpen}
          sx={{
            display: {
              lg: "none",
            },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            fullWidth
            id="org-select-menu-button"
            aria-controls="org-select-menu"
            aria-haspopup="true"
            aria-expanded={orgMenuOpen ? "true" : undefined}
            variant="contained"
            disableElevation
            onClick={handleClick}
            sx={{
              backgroundColor: "transparent",
              cursor: organizations?.length > 1 ? "pointer" : "default",
              whiteSpace: "nowrap",
              px: 1.5,
              py: 0.5,
              minHeight: "auto",
              height: 40,
            }}
            endIcon={
              organizations?.length > 1 ? (
                <ChevronDown sx={{ mt: 0.5 }} />
              ) : null
            }
          >
            {currentOrg?.branding?.logo_url ? (
              <Avatar
                src={currentOrg.branding.logo_url}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  bgcolor: "white",
                  boxShadow: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: "50%",
                  backgroundColor: "transparent",
                  border: "1px solid rgba(255, 255, 255, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: 1,
                }}
              >
                <ApartmentIcon
                  sx={{
                    fontSize: 18,
                    color: "white",
                    opacity: 0.7,
                  }}
                />
              </Box>
            )}
            {currentOrg ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: "white",
                    lineHeight: 1.25,
                    fontSize: "1.1rem",
                    ml: 1.25,
                  }}
                >
                  {currentOrg.displayName}
                </Typography>
                <OrganizationTier />
              </Box>
            ) : null}
          </Button>
          {organizations?.length > 1 ? (
            <StyledMenu
              id="org-select-menu"
              MenuListProps={{ "aria-labelledby": "org-select-menu-button" }}
              anchorEl={anchorEl}
              open={orgMenuOpen}
              onClose={handleClose}
            >
              <MenuItem onClick={triggerFacilityChange}>
                Change Facility
              </MenuItem>
            </StyledMenu>
          ) : null}
        </Box>

        <Grid flex={1} item xs={12} sx={{ zIndex: "modal" }}>
          {appAlert?.message && (
            <Grid sx={{ zIndex: "modal" }}>
              <Alert severity={appAlert?.variant}>{appAlert?.message}</Alert>
            </Grid>
          )}
        </Grid>

        <Box sx={{ ml: 1 }}>
          <ActionsMenu />
        </Box>

        <Box sx={{ ml: 1 }}>
          <ContentSearch />
        </Box>

        <Box sx={{ ml: 1 }}>
          {allowNotifications && <NotificationCenter />}
        </Box>

        <Box sx={{ ml: 2 }}>
          <AccountPopover />
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

export default DashboardNavbar;
